import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Knack purpose-built quick-add. ORIGINAL Horizon code.
 *
 * Why this exists: Horizon's native quick-add clones the PDP buy box into a modal via innerHTML.
 * That mangles our custom <knack-pack-selector> Web Component (it rendered collapsed, and the
 * native mobile reorganiser deleted the details column). Instead, each <knack-quick-add> trigger
 * fetches a dedicated section (sections/knack-quick-add.liquid) via the Section Rendering API and
 * shows that FRESH server HTML in a native <dialog> (free focus-trap, ESC, backdrop). The pack
 * selector's radios are natively selectable without its JS, so we read the checked tier and add it.
 *
 * Subscribe vs one-time: the pack tiers are the Subscribe & Save offer (Add to cart, with the tier's
 * selling plan). The pack selector also renders a "Buy once" line (hidden by default); we reveal it,
 * keep it pointed at the selected tier (cloning that card's pre-rendered one-time price), and add it
 * with no selling plan. We do NOT load the pack selector's own JS here - it scopes to the whole
 * document and relocates the one-time line onto any product form on the page.
 *
 * Fires Upstack view_content on open/selection, but deliberately does NOT send a custom Upstack
 * add_to_cart: the Upstack app embed's own automatic event detection already reports add-to-cart
 * independently of any theme code, so a custom call here double-counts every add (mirrors the PDP
 * pack selector; see DECISIONS.md).
 *
 * @element knack-quick-add
 * @attr {string} data-product-url - the product URL to fetch the quick-add section from.
 */
const SECTION_ID = 'knack-quick-add';

class KnackQuickAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-knack-quick-add-trigger]') : null;
    if (!trigger) return;
    event.preventDefault();
    const url = this.dataset.productUrl;
    if (url) KnackQuickAddDialog.instance().open(url, trigger);
  };
}

class KnackQuickAddDialog {
  /** @type {KnackQuickAddDialog | undefined} */
  static #instance;
  static instance() {
    return (this.#instance ??= new KnackQuickAddDialog());
  }

  #dialog;
  #content;
  /** @type {Element | null} */
  #returnFocus = null;
  #cache = new Map();

  constructor() {
    injectDialogStyles();
    this.#dialog = document.createElement('dialog');
    this.#dialog.className = 'knack-quick-add-dialog';
    this.#dialog.setAttribute('aria-label', 'Quick add');
    this.#dialog.innerHTML = `
      <button type="button" class="knack-quick-add-dialog__close" data-close aria-label="Close">
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>
      </button>
      <div class="knack-quick-add-dialog__content" data-content></div>`;
    document.body.appendChild(this.#dialog);
    this.#content = /** @type {HTMLElement} */ (this.#dialog.querySelector('[data-content]'));

    this.#dialog.querySelector('[data-close]')?.addEventListener('click', () => this.close());
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });
    this.#dialog.addEventListener('close', () => {
      if (this.#returnFocus instanceof HTMLElement) this.#returnFocus.focus();
      // Tear down the fetched section's DOM (not just hide it): it contains a real
      // <knack-pack-selector> instance that adds a document-level cart:update listener in its
      // connectedCallback. A closed <dialog> stays in the document, so without this the listener
      // never runs disconnectedCallback and stays live, double-firing Upstack add_to_cart on the
      // next add made anywhere else on the page (e.g. the PDP's own pack selector).
      this.#content.innerHTML = '';
    });
    this.#dialog.addEventListener('click', this.#onContentClick);
    this.#dialog.addEventListener('change', this.#onContentChange);
  }

  /**
   * @param {string} url
   * @param {Element} trigger
   */
  async open(url, trigger) {
    this.#returnFocus = trigger;
    this.#content.innerHTML = '<div class="knack-quick-add-dialog__loading" aria-busy="true" aria-live="polite">Loading…</div>';
    if (!this.#dialog.open) this.#dialog.showModal();

    // Shopify appends its own tracking parameters to product URLs on search results and filtered
    // collections (_pos, _sid, _ss, _fid), so `url` often already carries a query string. Appending
    // "?section_id=" to that produced two question marks: Shopify never parsed section_id, returned
    // the whole product page with a 200, and 350KB of a second copy of the site went into the
    // dialog with no working Add button. The 200 also meant the catch below never fired. Measured
    // 9 September against the live storefront: preserving the tracking parameters and setting
    // section_id as a proper parameter returns the same 14,390-byte section as a clean URL does.
    const endpoint = new URL(url, window.location.origin);
    endpoint.searchParams.set('section_id', SECTION_ID);
    // Cache per product, not per URL: the tracking parameters differ between surfaces and change
    // with every search session, so keying on the raw URL refetched the same modal endlessly.
    const cacheKey = endpoint.pathname;

    let html = this.#cache.get(cacheKey);
    if (html == null) {
      try {
        const res = await fetch(endpoint.toString());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        html = await res.text();
        this.#cache.set(cacheKey, html);
      } catch (error) {
        this.#content.innerHTML = '<p class="knack-quick-add-dialog__error">Sorry, we could not load this. Please try again.</p>';
        return;
      }
    }
    this.#content.innerHTML = html;
    this.#content.scrollTop = 0;
    // a11y: name the dialog by the product title now that it is in the DOM.
    if (this.#content.querySelector('#knack-qa-title')) {
      this.#dialog.setAttribute('aria-labelledby', 'knack-qa-title');
      this.#dialog.removeAttribute('aria-label');
    }
    this.#revealOneTime();
    this.#fireViewContent(this.#checkedPack());
  }

  /** The checked pack/subscribe radio in the dialog. @returns {HTMLInputElement | null} */
  #checkedPack() {
    const el = this.#content.querySelector('[data-pack-option]:checked');
    return el instanceof HTMLInputElement ? el : null;
  }

  /** Reveal the (hidden) one-time line, move it below the Add button, and sync it to the selection. */
  #revealOneTime() {
    const extras = this.#content.querySelector('[data-knack-onetime-extras]');
    if (!extras) return;
    const actions = this.#content.querySelector('.knack-quick-add__actions');
    if (actions) actions.insertAdjacentElement('afterend', extras);
    extras.removeAttribute('hidden');
    this.#syncBuyOnce();
  }

  /** Point the "Buy once" button at the selected tier + mirror that tier's one-time price markup. */
  #syncBuyOnce() {
    const packRadio = this.#checkedPack();
    if (!packRadio || packRadio.hasAttribute('data-onetime')) return;
    const buyOnce = this.#content.querySelector('[data-knack-buyonce]');
    if (!(buyOnce instanceof HTMLElement)) return;

    const variantId = packRadio.dataset.variantId || packRadio.value;
    if (variantId) buyOnce.dataset.variantId = variantId;
    if (packRadio.dataset.productId) buyOnce.dataset.productId = packRadio.dataset.productId;

    const srcTpl = packRadio.closest('label')?.querySelector('template.knack-pack-selector__onetime-src');
    const srcPrice = srcTpl?.content?.querySelector('.knack-price__current');
    const dest = buyOnce.querySelector('.knack-price__current');
    if (srcPrice && dest) dest.innerHTML = srcPrice.innerHTML;
  }

  #onContentChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-pack-option]')) {
      this.#syncBuyOnce();
      this.#fireViewContent(event.target);
    }
  };

  #onContentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const submit = target.closest('[data-knack-quick-add-submit]');
    if (submit instanceof HTMLElement) {
      event.preventDefault();
      const checked = this.#checkedPack();
      if (checked) {
        this.#addLine(checked.dataset.variantId, checked.dataset.sellingPlanId, checked.dataset.productId, submit);
        return;
      }
      // A trade modal has no pack options at all: it is a quantity picker, and the variant is
      // already decided. Without this branch the handler asked the shopper to choose from an empty
      // set and nothing ever reached the cart. Keyed on the marker the trade branch renders, so a
      // pack modal with nothing selected still gets the prompt below.
      const direct = this.#content.querySelector('[data-knack-quick-add-direct]');
      if (direct instanceof HTMLElement) {
        this.#addLine(direct.dataset.variantId, null, direct.dataset.productId, submit);
        return;
      }
      this.#status('Please choose an option.', true);
      return;
    }

    const buyOnce = target.closest('[data-knack-buyonce]');
    if (buyOnce instanceof HTMLElement) {
      event.preventDefault();
      // One-time: add the selected tier's variant with NO selling plan.
      this.#addLine(buyOnce.dataset.variantId, null, buyOnce.dataset.productId, buyOnce);
    }
  };

  /**
   * @param {string | undefined} variantId
   * @param {string | null | undefined} sellingPlanId
   * @param {string | undefined} productId
   * @param {HTMLElement} busyEl
   */
  async #addLine(variantId, sellingPlanId, productId, busyEl) {
    if (!variantId) {
      this.#status('Please choose an option.', true);
      return;
    }
    if (busyEl.getAttribute('aria-busy') === 'true') return;
    busyEl.setAttribute('aria-busy', 'true');
    this.#status('', false);

    const formData = new FormData();
    formData.set('id', variantId);
    // Trade products carry a minimum order and an increment, so the modal renders a quantity
    // selector and the chosen value has to reach the cart. Pack products have no selector, so the
    // fallback of 1 is still correct for them.
    // Scoped to the dialog's own content rather than the document: only one modal is ever open, but
    // a document-wide selector would be a standing hazard the day a second one is not.
    const qtyInput = this.#content.querySelector('input[name="quantity"]');
    const parsedQty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    const quantity = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
    formData.set('quantity', String(quantity));
    if (sellingPlanId) formData.set('selling_plan', sellingPlanId);
    // Hidden line-item property: marks the line as a subscription for the order-confirmation
    // email (knack-klaviyo-events / Klaviyo templates read this). All Loop plans are 4-weekly today.
    if (sellingPlanId) formData.set('properties[_delivery_frequency]', '4 weeks');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const cfg = fetchConfig('javascript', { body: formData });

    try {
      const res = await fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } });
      const data = await res.json();

      if (data?.status) {
        document.dispatchEvent(new CartErrorEvent('knack-quick-add', data.message, data.description, data.errors));
        this.#status(data.message || 'Could not add to cart.', true);
        return;
      }

      const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => undefined);
      this.close();
      document.dispatchEvent(
        new CartAddEvent(cart ?? undefined, 'knack-quick-add', {
          source: 'knack-quick-add',
          productId,
          // The resolved quantity, not a hardcoded 1: the cart bubble reads this, and a trade
          // buyer adding six cases would otherwise see the count move by one.
          itemCount: quantity,
          variantId,
          sections: data.sections,
        })
      );
      this.#firedVC().clear();
    } catch (error) {
      console.error('knack quick-add failed', error);
      this.#status('Could not add to cart. Please try again.', true);
    } finally {
      busyEl.removeAttribute('aria-busy');
    }
  }

  /* ---- Upstack tracking (guarded; inert until the app embed is enabled; mirrors the PDP pack selector) ---- */
  #firedVC() {
    window._upstackFiredVC = window._upstackFiredVC || new Set();
    return window._upstackFiredVC;
  }

  /** @param {string | undefined} variantId @param {number} cents @param {string | undefined} boxQuantity */
  #trackingPayload(variantId, cents, boxQuantity) {
    const ps = this.#content.querySelector('knack-pack-selector');
    const value = cents / 100;
    const currency = ps instanceof HTMLElement ? ps.dataset.currency : undefined;
    return {
      value,
      currency,
      items: [
        {
          id: ps instanceof HTMLElement ? ps.dataset.productId : undefined,
          variant: variantId,
          name: `${(ps instanceof HTMLElement && ps.dataset.productTitle) || ''} - ${boxQuantity || ''}`.trim(),
          sku: variantId,
          price: value,
          currency,
        },
      ],
    };
  }

  /** Fire Upstack view_content for a pack tier, once per variant. @param {HTMLInputElement | null} radio */
  #fireViewContent(radio) {
    if (typeof window._upstack !== 'function' || !radio || radio.hasAttribute('data-onetime')) return;
    const variantId = radio.dataset.variantId || radio.value;
    if (!variantId || this.#firedVC().has(variantId)) return;
    this.#firedVC().add(variantId);
    window._upstack('track', 'view_content', this.#trackingPayload(variantId, Number(radio.dataset.chargedCents || 0), radio.dataset.boxQuantity));
  }

  /** @param {string} message @param {boolean} isError */
  #status(message, isError) {
    const status = this.#content.querySelector('[data-knack-quick-add-status]');
    if (!status) return;
    status.textContent = message;
    if (isError) status.setAttribute('data-error', '');
    else status.removeAttribute('data-error');
  }

  close() {
    if (this.#dialog.open) this.#dialog.close();
  }
}

let stylesInjected = false;
function injectDialogStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .knack-quick-add-dialog {
      width: min(880px, 92vw); max-height: 90vh; padding: 0; border: 0; border-radius: 20px;
      background: var(--color-background, #fff); color: var(--color-foreground, #111);
      box-shadow: 0 24px 60px rgba(0,0,0,0.28); overflow: visible;
    }
    .knack-quick-add-dialog::backdrop { background: rgba(0,0,0,0.45); backdrop-filter: blur(2px); }
    .knack-quick-add-dialog__content { padding: clamp(1.25rem, 3vw, 2rem); max-height: 90vh; overflow-y: auto; overscroll-behavior: contain; border-radius: 20px; }
    .knack-quick-add-dialog__close {
      position: absolute; top: 0.75rem; right: 0.75rem; z-index: 2;
      inline-size: 40px; block-size: 40px; display: grid; place-items: center;
      border: 0; border-radius: 999px; cursor: pointer;
      background: var(--color-background, #fff); color: var(--color-foreground, #111);
      box-shadow: 0 2px 8px rgba(0,0,0,0.18);
    }
    .knack-quick-add-dialog__close:hover { filter: brightness(0.95); }
    .knack-quick-add-dialog__close:focus-visible { outline: 2px solid var(--color-foreground); outline-offset: 2px; }
    .knack-quick-add-dialog__loading, .knack-quick-add-dialog__error { padding: 3rem 1rem; text-align: center; font-family: var(--font-body--family); }
    @media (prefers-reduced-motion: no-preference) {
      .knack-quick-add-dialog[open] { animation: knack-qa-in 0.2s ease both; }
      @keyframes knack-qa-in { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: none; } }
    }
    @media (max-width: 749px) {
      .knack-quick-add-dialog { width: 100vw; max-width: 100vw; max-height: 92vh; margin: auto auto 0; border-radius: 20px 20px 0 0; }
      .knack-quick-add-dialog__content { border-radius: 20px 20px 0 0; }
    }
  `;
  document.head.appendChild(style);
}

if (!customElements.get('knack-quick-add')) {
  customElements.define('knack-quick-add', KnackQuickAdd);
}
